import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Loader2, Link2, Package, Sparkles, AlertTriangle } from 'lucide-react';
import { functions } from '../../config/firebase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FASH_LOGO } from '../../constants/brand';

/**
 * Public, shareable "Connect your Shopify store to FASH" landing page.
 * No login required — the store owner enters their myshopify domain, approves
 * on Shopify, then lands on the finish page to create/claim their FASH boutique
 * and import products. Route: /connect/shopify
 */
export function ConnectShopifyPage() {
  const [shopDomain, setShopDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    const raw = shopDomain.trim();
    if (!raw) {
      setError('Enter your store domain');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Backend resolves a custom domain / handle into the canonical
      // *.myshopify.com domain, so the owner can just type their website.
      const startOAuth = httpsCallable(functions, 'shopifyPublicAuthStart');
      const result = await startOAuth({ shop: raw });
      const { authUrl } = result.data as { authUrl: string };
      window.location.href = authUrl;
    } catch (err: any) {
      setError(err?.message || 'Could not find your Shopify store. Try your .myshopify.com address.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={FASH_LOGO} alt="Fash" className="h-12 w-12 object-contain mx-auto mb-3" />
          <h1 className="text-2xl font-serif text-heading">Connect your Shopify store</h1>
          <p className="text-sm text-body mt-1.5">
            Link your store to FASH and start selling with same-day local delivery.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 space-y-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-heading">Your store website or domain</label>
            <Input
              type="text"
              placeholder="yourstore.com or your-store.myshopify.com"
              value={shopDomain}
              onChange={(e) => {
                setShopDomain(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
              className="font-mono text-sm"
              autoComplete="off"
              autoFocus
            />
            <p className="text-xs text-body">
              Paste your store website (e.g. <span className="font-mono">yourstore.com</span>) — we'll find your
              Shopify store automatically. Your <span className="font-mono">.myshopify.com</span> address works too.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
              <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          <Button onClick={handleConnect} disabled={loading || !shopDomain.trim()} className="w-full" size="lg">
            {loading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Finding your store...</>
            ) : (
              <><Link2 className="h-4 w-4 mr-2" />Connect with Shopify</>
            )}
          </Button>

          <p className="text-[11px] text-center text-body">
            You'll approve access on Shopify, then finish setting up your FASH boutique.
          </p>
        </div>

        {/* Steps */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          <Step icon={Link2} label="Connect" desc="Approve on Shopify" />
          <Step icon={Package} label="Import" desc="Pick your products" />
          <Step icon={Sparkles} label="Go live" desc="Sell on FASH" />
        </div>
      </div>
    </div>
  );
}

function Step({ icon: Icon, label, desc }: { icon: React.ElementType; label: string; desc: string }) {
  return (
    <div className="bg-white/70 rounded-xl border border-gray-100 p-3 text-center">
      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-2">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <p className="text-xs font-semibold text-heading">{label}</p>
      <p className="text-[10px] text-body mt-0.5">{desc}</p>
    </div>
  );
}
